/**
 * Sync Controls Component
 *
 * Displays sync status and provides manual sync trigger for cloud capacity data.
 * Shows last sync timestamp and sync job status.
 */

import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import toast from 'react-hot-toast'
import { RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { triggerSync, getLatestSyncJob } from '../../services/capacity-sync'
import type { SyncJobStatus } from '../../types/capacity'

/** Format relative time (e.g., "5 minutes ago") */
const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
}

/** Get status badge variant */
const getStatusVariant = (
    status: SyncJobStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
        case 'completed':
            return 'default'
        case 'running':
        case 'pending':
            return 'secondary'
        case 'failed':
            return 'destructive'
        default:
            return 'outline'
    }
}

/** Get status icon */
const StatusIcon = ({ status }: { status: SyncJobStatus }) => {
    switch (status) {
        case 'completed':
            return <CheckCircle className="h-3 w-3" />
        case 'running':
        case 'pending':
            return <Loader2 className="h-3 w-3 animate-spin" />
        case 'failed':
            return <XCircle className="h-3 w-3" />
        default:
            return null
    }
}

/** Polling intervals (ms) */
const POLL_INTERVAL_SYNCING = 3000 // 3s when actively syncing
const POLL_INTERVAL_IDLE = 30000 // 30s when idle
const SYNC_CHECK_DELAY = 2000 // 2s delay before checking sync result

const SyncControls = () => {
    const queryClient = useQueryClient()
    const [isSyncing, setIsSyncing] = useState(false)
    const isMountedRef = useRef(true)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    // Fetch latest sync job
    const { data: latestJob, refetch: refetchJob } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CAPACITY_SYNC_JOBS, 'latest'],
        queryFn: getLatestSyncJob,
        refetchInterval: isSyncing ? POLL_INTERVAL_SYNCING : POLL_INTERVAL_IDLE,
    })

    // Trigger sync mutation
    const syncMutation = useMutationWithHandler(
        {
            mutationFn: triggerSync,
            onMutate: () => {
                setIsSyncing(true)
            },
            onSuccess: (result) => {
                toast.success(result.message || 'Sync started')
                refetchJob()
            },
            onError: () => {
                if (isMountedRef.current) {
                    setIsSyncing(false)
                }
            },
            onSettled: () => {
                // Check sync result after delay (safe cleanup)
                timeoutRef.current = setTimeout(() => {
                    if (!isMountedRef.current) return
                    refetchJob().then(({ data: job }) => {
                        if (!isMountedRef.current) return
                        if (job?.status === 'completed') {
                            setIsSyncing(false)
                            queryClient.invalidateQueries({
                                queryKey: [QUERY_KEYS.CAPACITY_ALL],
                            })
                            toast.success(
                                `Synced ${job.zones_synced} zones, ${job.hosts_synced} hosts`
                            )
                        } else if (job?.status === 'failed') {
                            setIsSyncing(false)
                            toast.error(job.error_message || 'Sync failed')
                        }
                    })
                }, SYNC_CHECK_DELAY)
            },
        },
        { showErrorToast: true }
    )

    const handleSync = () => {
        syncMutation.mutate()
    }

    // Update isSyncing based on job status
    const isJobRunning =
        latestJob?.status === 'running' || latestJob?.status === 'pending'
    const effectiveIsSyncing = isSyncing || isJobRunning

    return (
        <div className="flex items-center gap-3">
            {/* Last sync info */}
            {latestJob && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                <span>
                                    {latestJob.completed_at
                                        ? formatRelativeTime(
                                              latestJob.completed_at
                                          )
                                        : latestJob.started_at
                                          ? 'syncing...'
                                          : 'pending'}
                                </span>
                                <Badge
                                    variant={getStatusVariant(latestJob.status)}
                                    className="gap-1"
                                >
                                    <StatusIcon status={latestJob.status} />
                                    {latestJob.status}
                                </Badge>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <div className="text-xs space-y-1">
                                <p>
                                    <strong>Last sync:</strong>{' '}
                                    {latestJob.completed_at
                                        ? new Date(
                                              latestJob.completed_at
                                          ).toLocaleString()
                                        : 'In progress'}
                                </p>
                                <p>
                                    <strong>Zones:</strong>{' '}
                                    {latestJob.zones_synced}/
                                    {latestJob.zones_requested}
                                </p>
                                <p>
                                    <strong>Hosts:</strong>{' '}
                                    {latestJob.hosts_synced}
                                </p>
                                {latestJob.error_message && (
                                    <p className="text-destructive">
                                        <strong>Error:</strong>{' '}
                                        {latestJob.error_message}
                                    </p>
                                )}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}

            {/* Sync button */}
            <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={effectiveIsSyncing || syncMutation.isPending}
            >
                <RefreshCw
                    className={cn(
                        'h-4 w-4 mr-2',
                        effectiveIsSyncing && 'animate-spin'
                    )}
                />
                {effectiveIsSyncing ? 'Syncing...' : 'Sync Now'}
            </Button>
        </div>
    )
}

export default SyncControls
